// src/pages/admin/OdesDetailPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  getOrderByIdNormalized, // ⬅️ CAMBIADO: Usar versión con inteligencia
  getBanks,
  getSuppliers,
  type OC,
  type Banco,
  type Proveedor,
  formatAxiosError,
} from "../../services/api";

function fmtMoney(n: number | undefined | null) {
  const v = Number(n || 0);
  return v.toLocaleString("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 2 });
}

const LIMIT_PORCENTAJE = 33; 

const OdesDetailPage: React.FC = () => {
  const params = useParams<{ id: string }>();
  const [order, setOrder] = useState<OC | null>(null);
  const [banks, setBanks] = useState<Banco[]>([]);
  const [suppliers, setSuppliers] = useState<Proveedor[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const isImage = (u?: string) => !!u && /^(data:image\/|.*\.(png|jpe?g|webp|gif)(\?.*)?)$/i.test(u || "");

  /** ===== Mapeos para visualización ===== */
  const bankMap = useMemo(() => new Map(banks.map((b) => [String(b.id), `${b.nombre}${b.cuenta ? " - " + b.cuenta : ""}`])), [banks]);
  const supplierMap = useMemo(() => new Map(suppliers.map((s) => [String(s.id), s.nombre])), [suppliers]);

  const getBankName = (id?: any) => bankMap.get(String(id ?? "")) ?? `ID #${id ?? ""}`;
  const getSupplierName = (id?: any) => supplierMap.get(String(id ?? "")) ?? `ID #${id ?? ""}`;

  /** ===== Gastos Ordenados ===== */
  const expensesSorted = useMemo(() => {
    return [...(order?.expenses || [])].sort((a, b) => 
      new Date(b.created || "").getTime() - new Date(a.created || "").getTime()
    );
  }, [order]);

  /** ===== Cálculos de Rentabilidad ===== */
  const porcentajeGasto = useMemo(() => {
    const divisor = order?.presupuestoTotal || 0;
    if (divisor === 0) return 0;
    return (order!.totalEstimado / divisor) * 100;
  }, [order]);

  /** ===== Efectos UI ===== */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => (e.key === "Escape" ? setSelectedImage(null) : null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /** ===== Carga de Datos ===== */
  useEffect(() => {
    const fetchAll = async () => {
      if (!params.id) return;
      setLoading(true);
      setMsg(null);
      try {
        const [normOrder, banksData, suppliersData] = await Promise.all([
          getOrderByIdNormalized(Number(params.id)), // ⬅️ Carga normalizada
          getBanks(),
          getSuppliers(),
        ]);
        setOrder(normOrder);
        setBanks(banksData);
        setSuppliers(suppliersData);
      } catch (err) {
        const flat = formatAxiosError(err);
        setMsg(`❌ Error al cargar detalle: ${flat.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [params.id]);

  const exportCSV = () => {
    if (!order) return;
    const rows = [
      ["ID", "Fecha", "Código", "Banco", "Proveedor", "Monto"],
      ...expensesSorted.map((e) => [
        e.id,
        new Date(e.created).toLocaleString(),
        e.code,
        getBankName(e.bankId),
        getSupplierName(e.supplierId),
        e.amountSpent.toFixed(2),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Detalle_Gastos_OC_${order.id}.csv`;
    a.click();
  };

  if (loading) return <div className="p-20 text-center text-white text-xl animate-pulse">Cargando detalles de la orden...</div>;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 pt-24">
      <div className="mb-6 flex items-center justify-between">
        <Link to="/admin/odes" className="flex items-center gap-2 text-indigo-300 hover:text-white transition-colors">
          <span className="text-xl">←</span> Volver al listado
        </Link>
        <button onClick={exportCSV} className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg border border-white/20 transition-all text-sm font-bold">
          📥 Exportar Gastos (CSV)
        </button>
      </div>

      {/* HEADER DE LA ORDEN */}
      <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-6 mb-8 backdrop-blur-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-3xl font-black text-white">{order?.folio}</h1>
              <span className="px-3 py-1 rounded-full bg-indigo-500 text-white text-[10px] font-black uppercase">
                {order?.statusName}
              </span>
            </div>
            <p className="text-slate-400 text-sm">ID del Sistema: {order?.id} • Creado el {new Date(order?.created || '').toLocaleDateString()}</p>
          </div>
          <div className="text-right">
            <p className="text-slate-400 text-xs uppercase font-bold tracking-widest">Presupuesto Total</p>
            <p className="text-3xl font-black text-emerald-400">{fmtMoney(order?.presupuestoTotal)}</p>
          </div>
        </div>
      </div>

      {msg && <div className="mb-6 rounded-xl bg-rose-500/20 border border-rose-500/50 p-4 text-rose-200 text-sm">{msg}</div>}

      {/* INDICADORES FINANCIEROS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
          <p className="text-slate-500 text-xs font-bold uppercase mb-1">Total Gastado</p>
          <p className="text-2xl font-black text-slate-900">{fmtMoney(order?.totalEstimado)}</p>
          <div className="mt-2 text-[11px] text-slate-400">Suma de todos los tickets registrados</div>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
          <p className="text-slate-500 text-xs font-bold uppercase mb-1">Diferencia / Saldo</p>
          <p className={`text-2xl font-black ${(order?.presupuestoTotal || 0) - (order?.totalEstimado || 0) < 0 ? 'text-rose-600' : 'text-indigo-600'}`}>
            {fmtMoney((order?.presupuestoTotal || 0) - (order?.totalEstimado || 0))}
          </p>
          <div className="mt-2 text-[11px] text-slate-400">Margen respecto al presupuesto</div>
        </div>

        <div className={`rounded-2xl p-5 shadow-sm border ${porcentajeGasto > LIMIT_PORCENTAJE ? "bg-rose-50 border-rose-200" : "bg-white border-slate-100"}`}>
          <p className="text-slate-500 text-xs font-bold uppercase mb-1">% de Ejecución</p>
          <div className="flex items-center gap-3">
            <p className={`text-2xl font-black ${porcentajeGasto > 100 ? 'text-rose-700' : 'text-slate-900'}`}>
              {porcentajeGasto.toFixed(1)}%
            </p>
            {porcentajeGasto > 100 && <span className="bg-rose-600 text-white text-[10px] px-2 py-0.5 rounded font-bold uppercase animate-bounce">Excedido</span>}
          </div>
          <div className="mt-2 h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
             <div className={`h-full ${porcentajeGasto > 100 ? 'bg-rose-600' : 'bg-indigo-500'}`} style={{ width: `${Math.min(porcentajeGasto, 100)}%` }} />
          </div>
        </div>
      </div>

      {/* SECCIÓN DE PRODUCTOS SOLICITADOS (NUEVA) */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
          <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <span className="text-lg">🛒</span> Productos Solicitados en la Orden
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase font-bold">
              <tr>
                <th className="px-6 py-3">Typo de Producto</th>
                <th className="px-6 py-3">Descripción</th>
                <th className="px-6 py-3 text-center">Cantidad</th>
                <th className="px-6 py-3 text-right">Costo Unit.</th>
                <th className="px-6 py-3 text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {order?.items && order.items.length > 0 ? (
                order.items.map((item) => (
                  <tr key={item.id} className="hover:bg-indigo-50/30 transition-colors">
                    <td className="px-6 py-4 font-bold text-slate-700">{item.tipo}</td>
                    <td className="px-6 py-4 font-bold text-slate-700">{item.nombre}</td>
                    <td className="px-6 py-4 text-center font-mono">{item.cantidad}</td>
                    <td className="px-6 py-4 text-right text-slate-600">{fmtMoney(item.precioUnitario)}</td>
                    <td className="px-6 py-4 text-right font-black text-slate-900">{fmtMoney(item.totalItem)}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={4} className="px-6 py-10 text-center text-slate-400 italic">No hay productos detallados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* LISTADO DE GASTOS REGISTRADOS (TICKETS) */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
          <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <span className="text-lg">🧾</span> Historial de Gastos y Evidencias
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase font-bold">
              <tr>
                <th className="px-6 py-3">Fecha y Hora</th>
                <th className="px-6 py-3">Código/Ref</th>
                <th className="px-6 py-3">Proveedor / Banco</th>
                <th className="px-6 py-3 text-right">Monto</th>
                <th className="px-6 py-3 text-center">Evidencia</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {expensesSorted.length > 0 ? (
                expensesSorted.map((g) => (
                  <tr key={g.id} className="hover:bg-slate-50 transition-colors text-slate-700">
                    <td className="px-6 py-4 text-xs">{new Date(g.created).toLocaleString()}</td>
                    <td className="px-6 py-4 font-mono font-bold text-indigo-600">{g.code}</td>
                    <td className="px-6 py-4">
                      <div className="font-bold">{getSupplierName(g.supplierId)}</div>
                      <div className="text-[10px] text-slate-400">{getBankName(g.bankId)}</div>
                    </td>
                    <td className="px-6 py-4 text-right font-black">{fmtMoney(g.amountSpent)}</td>
                    <td className="px-6 py-4 text-center">
                      {g.evidenceFile ? (
                        /*isImage(g.evidenceFile) || */true ? (
                          <img src={`data:image/png;base64,${g.evidenceFile}`} alt="Imagen" className="h-10 w-10 object-cover rounded shadow-sm mx-auto cursor-zoom-in hover:scale-110 transition-transform" onClick={() => {setSelectedImage(g.evidenceFile!);
                            console.log('AM', g.evidenceFile);
                          }} />
                        ) : (
                          <a href={g.evidenceFile} target="_blank" rel="noreferrer" className="text-indigo-600 text-xs font-bold underline">PDF</a>
                        )
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-400 italic">No se han registrado gastos aún</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL DE IMAGEN */}
      {selectedImage && (
        <div className="fixed inset-0 bg-slate-900/90 flex items-center justify-center z-[100] p-4 animate-in fade-in duration-200" onClick={() => setSelectedImage(null)}>
          <div className="relative max-w-4xl w-full bg-white rounded-3xl p-2 shadow-2xl" onClick={e => e.stopPropagation()}>
            <img src={`data:image/png;base64,${selectedImage}`} alt="Evidencia Full" className="max-h-[85vh] w-full object-contain rounded-2xl" />
            <button onClick={() => setSelectedImage(null)} className="absolute -top-4 -right-4 bg-rose-500 text-white w-10 h-10 rounded-full flex items-center justify-center shadow-lg hover:bg-rose-600 transition-colors">✕</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OdesDetailPage;